import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import HttpService from '../../../services/HttpService';
import '../../../css/Pages.css';

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

/**
 * Hook for fetching interfaces when subscription is expanded
 */
const useInterfacesFetch = (abonnementId, isOpen, showInactive) => {
  const [interfaces, setInterfaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const fetchInterfaces = async () => {
      const axios = HttpService.getAxiosClient();
      const url = `/Statistieken/Abo?aboId=${abonnementId}`;

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(url);
        let fetchedInterfaces = response.data.interfaces || [];

        // Filter by active status if needed
        if (!showInactive) {
          fetchedInterfaces = fetchedInterfaces.filter(
            int => int.status === 'Actief'
          );
        }

        setInterfaces(fetchedInterfaces);
      } catch (err) {
        setError(err.message);
        setInterfaces([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInterfaces();
  }, [abonnementId, isOpen, showInactive]);

  return { interfaces, loading, error };
};

/**
 * Hook for calculating message totals based on time period
 */
const useMessageTotal = (berichtenPerMaand, selectedYear, selectedMonth) => {
  return useMemo(() => {
    if (!berichtenPerMaand || berichtenPerMaand.length === 0) return 0;

    return berichtenPerMaand.reduce((total, item) => {
      // Filter by specific month and year
      if (selectedMonth > 0) {
        if (item.maand === selectedMonth && item.jaar === selectedYear) {
          return total + item.aantal;
        }
        return total;
      }

      // Filter by year only
      if (selectedYear > 0) {
        if (item.jaar === selectedYear) {
          return total + item.aantal;
        }
        return total;
      }

      // No filter - return all
      return total + item.aantal;
    }, 0);
  }, [berichtenPerMaand, selectedYear, selectedMonth]);
};

/**
 * Hook for calculating interface-specific message count
 */
const useInterfaceMessageCount = (berichtenPerMaand, selectedYear, selectedMonth) => {
  return useMemo(() => {
    if (!berichtenPerMaand) return 0;

    // If specific month selected, find exact match
    if (selectedMonth > 0 && selectedYear > 0) {
      const match = berichtenPerMaand.find(
        item => item.jaar === selectedYear && item.maand === selectedMonth
      );
      return match?.aantal || 0;
    }

    // Otherwise calculate total for year or all time
    return berichtenPerMaand.reduce((total, item) => {
      if (selectedYear > 0) {
        return item.jaar === selectedYear ? total + item.aantal : total;
      }
      return total + item.aantal;
    }, 0);
  }, [berichtenPerMaand, selectedYear, selectedMonth]);
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SubscriptionRow = ({
  abonnement,
  isOpen,
  totalMessages,
  onClick,
}) => {
  const formatDate = (dateString) => {
    return dateString ? dateString.split('T')[0] : '';
  };

  return (
    <tr
      className="subscription-row"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-expanded={isOpen}
    >
      <td>
        <span className="subscription-toggle">
          <span className="subscription-toggle-icon">{isOpen ? '▼' : '▶'}</span>
          {abonnement.naam}
        </span>
      </td>
      <td>{formatDate(abonnement.startdatum)}</td>
      <td>{formatDate(abonnement.einddatum)}</td>
      <td>{abonnement.status}</td>
      <td>{abonnement.interfaceCount}</td>
      <td>{totalMessages}</td>
      <td></td>
    </tr>
  );
};

const InterfaceHeaderRow = () => {
  const { t } = useTranslation();
  return (
    <tr className="interface-header-row">
      <td>{t('dashboard.status')}</td>
      <td>{t('interface.sender')}</td>
      <td>{t('interface.receiver')}</td>
      <td>{t('subscriptions.status')}</td>
      <td>{t('subscriptions.startDate')}</td>
      <td>{t('subscriptions.endDate')}</td>
      <td>{t('dashboard.messages')}</td>
    </tr>
  );
};

const InterfaceRow = ({
  interfaceObj,
  isSelected,
  selectedYear,
  selectedMonth,
  onClick,
}) => {
  const messageCount = useInterfaceMessageCount(
    interfaceObj.berichtenPerMaand,
    selectedYear,
    selectedMonth
  );

  const formatDate = (dateString) => {
    return dateString ? dateString.split('T')[0] : '';
  };

  return (
    <tr
      className={`interface-row ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <td>{interfaceObj.omschrijving}</td>
      <td>{interfaceObj.verzenderNaam}</td>
      <td>{interfaceObj.ontvangerNaam}</td>
      <td>{interfaceObj.status}</td>
      <td>{formatDate(interfaceObj.startdatum)}</td>
      <td>{formatDate(interfaceObj.einddatum)}</td>
      <td>{messageCount}</td>
    </tr>
  );
};

const LoadingRow = () => {
  const { t } = useTranslation();
  return (
    <tr className="loading-row">
      <td colSpan={7}>
        <div className="loading-spinner">
          <div className="spinner"></div>
          {t('common.loading')}
        </div>
      </td>
    </tr>
  );
};

const EmptyRow = () => {
  const { t } = useTranslation();
  return (
    <tr className="empty-row">
      <td colSpan={7}>
        {t('interface.interfaces')} - {t('common.noDataAvailable')}
      </td>
    </tr>
  );
};

const ErrorRow = ({ message }) => {
  const { t } = useTranslation();
  if(message.includes("400")){
    return (
      <tr className="error-row error-400">
        <td colSpan={7}>
          {t('interface.interfaces')} - {t('common.noDataAvailable')}
        </td>
      </tr>
    )
  }
  else
    return (
      <tr className="error-row error-other">
        <td colSpan={7}>
          {t('common.error')}: {message}
        </td>
      </tr>
    )
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AbonnementCollapsibleKlantDashboard = ({
  abonnement,
  year = 0,
  month = 0,
  showInactive = true,
  changeData,
  changeDataAbo,
  resetData,
  triggerKey,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedInterfaceId, setSelectedInterfaceId] = useState(null);

  const { interfaces, loading, error } = useInterfacesFetch(
    abonnement.id,
    isOpen,
    showInactive
  );

  const totalMessages = useMessageTotal(
    abonnement.berichtenPerMaand,
    year,
    month
  );

  // Handle toggle of subscription expansion
  const handleToggle = useCallback(() => {
    if (!isOpen) {
      // Opening - select abonnement and clear interface selection
      changeDataAbo(abonnement);
      setSelectedInterfaceId(null);
    } else {
      // Closing - reset to customer level data
      resetData();
      setSelectedInterfaceId(null);
    }
    setIsOpen(!isOpen);
  }, [isOpen, abonnement, changeDataAbo, resetData]);

  // Handle interface selection
  const handleInterfaceSelect = useCallback(
    (interfaceObj) => {
      changeData(interfaceObj);
      setSelectedInterfaceId(interfaceObj.id);
    },
    [changeData]
  );

  // Reset selection when triggerKey changes (year/month filter changed)
  useEffect(() => {
    if (triggerKey) {
      setSelectedInterfaceId(null);
    }
  }, [triggerKey]);

  return (
    <tbody className="border-2 border-gray-300">
      <SubscriptionRow
        abonnement={abonnement}
        isOpen={isOpen}
        totalMessages={totalMessages}
        onClick={handleToggle}
      />

      {isOpen && loading && <LoadingRow />}

      {isOpen && error && <ErrorRow message={error} />}

      {isOpen && !loading && !error && interfaces.length === 0 && <EmptyRow />}

      {isOpen && !loading && !error && interfaces.length > 0 && (
        <>
          <InterfaceHeaderRow />
          {interfaces.sort((a, b) => a.omschrijving.localeCompare(b.omschrijving)).map((interfaceObj) => (
            <InterfaceRow
              key={interfaceObj.id}
              interfaceObj={interfaceObj}
              isSelected={selectedInterfaceId === interfaceObj.id}
              selectedYear={year}
              selectedMonth={month}
              onClick={() => handleInterfaceSelect(interfaceObj)}
            />
          ))}
        </>
      )}
    </tbody>
  );
};

export default AbonnementCollapsibleKlantDashboard;